import { ApplicationRef, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ApiService } from 'core/api/api.service';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { NotificationService } from 'core/notification/notification.service';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { environment } from '../../../../../environments/environment';
import { PaymentStatus } from '../entities/paymentStatuses';
import { CvvModalComponent } from '../payment-chase/cvv-modal/cvv-modal.component';
import { PaymentChaseComponent } from '../payment-chase/payment-chase.component';

const paymentDialog = 'isc-payment-dialog';
const updateCreditCardWindowStatus = 'updateCreditCardWindowStatus';

declare global {
  interface Window {
    completeCREPayment: any;
    cancelCREPayment: any;
    creHandleErrors: any;
    whatCVV2: any;
    ipcRenderer: any;
    startPayment?: any;
    cancelPayment?: any;
    completePayment?: any;
    handleTransactionErrors?: any;
    whatsThis?: any;
    hpfReady?: any;
  }
}

const gatewayErrorStatusCode = 2300;

const paymentErrors = {
  nameRequired: 'Name required',
  amountInvalid: 'Amount entered is invalid',
  ccRequired: 'Credit card required',
  invalidNumber: 'Invalid credit card number',
  invalidType: 'Invalid credit card type',
  invalidMonth: 'Invalid credit card month',
  invalidExpirationYear: 'Invalid credit card expiration year',
  invalidCVC: 'Invalid CVC',
  invalidCaptcha: 'Invalid Captcha',
  CVCRequired: 'CVC required',
  errorOccurred: 'Payment declined, please contact your financial institution',
  invalidExpirationDate: 'Invalid credit card expiration date',
  addressRequired: 'Address required',
  cityRequired: 'City required',
  stateRequired: 'State required',
  zipRequired: 'Zip code required',
  invalidZip: 'Invalid Zip code',
  countryRequired: 'Country required'
};

const errorMappings = {
  200: paymentErrors.nameRequired,
  300: paymentErrors.amountInvalid,
  310: paymentErrors.ccRequired,
  315: paymentErrors.invalidNumber,
  320: paymentErrors.invalidType,
  330: paymentErrors.invalidMonth,
  340: paymentErrors.invalidExpirationYear,
  350: paymentErrors.invalidCVC,
  355: paymentErrors.CVCRequired,
  357: paymentErrors.invalidCVC,
  360: paymentErrors.errorOccurred,
  370: paymentErrors.invalidExpirationDate,
  380: paymentErrors.invalidCaptcha,
  500: paymentErrors.addressRequired,
  510: paymentErrors.cityRequired,
  520: paymentErrors.stateRequired,
  530: paymentErrors.zipRequired,
  531: paymentErrors.invalidZip,
  550: paymentErrors.countryRequired
};

@Injectable({
  providedIn: 'root'
})
export class ChasePaymentService {

  private paymentParams = {
    quoteId: null,
    paymentToken: null,
    successCallback: null,
    cancelCallback: null,
    errorCallback: null
  };

  private appStrings: AppLocalization;
  private dialogRef: MatDialogRef<PaymentChaseComponent>;
  private hpfCallbacksRegistered = false;

  constructor(
    private appInfoService: AppInfoService,
    private matDialogService: MatDialog,
    private app: ApplicationRef,
    private notificationService: NotificationService,
    private apiService: ApiService,
    private appStateService: AppStateService,
    private deviceService: DeviceDetectorService,
    private router: Router
  ) {
    this.appStrings = this.appInfoService.appStrings;
    this.registerLegacyCallbacks();
    this.ensureHpfCallbacksRegistered();
    this.appInfoService.userDataChanged$?.subscribe(() => this.ensureHpfCallbacksRegistered());
    if (!this.appInfoService.isNSLocaleSelected()
      && this.appStateService.state.isElectron
      && typeof window.ipcRenderer?.on === 'function') {
      window.ipcRenderer.on('ChaseCallbackQueryString', (event, message) => {
        const paymentFrame = document.getElementsByClassName('payment-frame')[0];
        paymentFrame.setAttribute('src', `assets/resources/payment/Callback.html?${message}`);
      });
      window.ipcRenderer.on('ChaseWhatCvvCallback', () => {
        this.whatCVV2();
      });
      window.ipcRenderer.on('ChaseHandleErrorsCallback', (event, message) => {
        this.creHandleErrors(message);
        this.cancelCREPayment();
      });
    }
  }

  /**
   * Handles CC payment - opens a payment iframe
   * @param type quoteId - The order number
   * @param type paymentToken - The uID generated from the server
   * @param type callback - A callback to be called when ready
   */
  handlePayment(quoteId, paymentToken, successCallback, cancelCallback, errorCallback) {
    this.ensureHpfCallbacksRegistered();
    this.paymentParams.quoteId = quoteId;
    this.paymentParams.paymentToken = paymentToken;
    this.paymentParams.successCallback = successCallback;
    this.paymentParams.cancelCallback = cancelCallback;
    this.paymentParams.errorCallback = errorCallback;

    let chaseUrl = `${environment.paymentChaseUrl}?uID=${paymentToken}`;
    if (this.appInfoService.isNSLocaleSelected()) {
      chaseUrl = `${environment.paymentChaseUrlHPS3}?uid=${paymentToken}`;
    }

    const dialogParams = {
      closeOnNavigation: false,
      autoFocus: true,
      data: {
        url: chaseUrl
      },
      disableClose: true,
      panelClass: this.deviceService.isMobile() ? ['ds', 'fullWidth', 'fullScreen', 'chasePayment'] : paymentDialog
    };

    this.dialogRef = this.matDialogService.open(PaymentChaseComponent, dialogParams);
    this.setCreditCardPopUpStatus(true);
    return this.dialogRef.beforeClosed().toPromise();
  }


  private setCreditCardPopUpStatus(status: boolean) {
    if (this.appStateService.state.isElectron && window.ipcRenderer) {
      window.ipcRenderer.send(updateCreditCardWindowStatus, status);
    }
  }

  /**
   * Completes payment
   * Called when the iframe closes and payment was successful
   * Updates the payment and order status to successful
   */
  private completeCREPayment() {
    this.dialogRef.close();
    this.setCreditCardPopUpStatus(false);
    this.app.tick();

    this.paymentParams.successCallback({
      QuoteId: this.paymentParams.quoteId,
      UID: this.paymentParams.paymentToken,
      Status: PaymentStatus.Completed
    });
    this.app.tick();
  }

  /**
   * Cancel Payment - Called when the customer clicks the cancel button.
   * The function closes the iFrame and indicates that the customer’s card was not charged.
   */
  private cancelCREPayment() {
    this.dialogRef.close();
    this.setCreditCardPopUpStatus(false);
    this.app.tick();
    this.paymentParams.cancelCallback({
      QuoteId: this.paymentParams.quoteId,
      UID: this.paymentParams.paymentToken,
      Status: PaymentStatus.Canceled
    });
  }

  closeCREPayment() {
    this.dialogRef.close();
    this.setCreditCardPopUpStatus(false);
  }

  /**
   * Called when the customer clicks the link for more information about CVV2 numbers.
   * The function displays a popup window, which describes what the CVV2 number is and where to find it on the card.
   */
  private whatCVV2() {
    const dialogRef = this.matDialogService.open(CvvModalComponent, {
      width: 'auto'
    });
    this.app.tick();
    return dialogRef.beforeClosed().toPromise();
  }

  /**
   * Called when a transaction error occurs.
   * @param string errors - The concatenated errors
   * @returns boolean False
   */
  private creHandleErrors(errors) {
    let err = '';
    try {
      errors = errors.trim('|');
      const arrErrors = errors.split('|');
      let errorMessage = '';

      // cycle through the error codes and get the messages
      arrErrors.forEach(code => {
        // remove errorCode= from error, specially when the app is used in Electron mode
        if (code.indexOf('errorCode=') !== -1) {
          code = code.replace('errorCode=', '');
        }
        if (errorMappings[code]) {
          errorMessage += errorMappings[code] + '. ';
        }
      });
      // Sets the error message
      err = errorMessage;
    } catch (e) {
      err = this.appStrings.quoteDetails_PaymentError;
    }

    this.apiService.logCustomErrors(`FE Chase failed: ${err} - QuoteId: ${this.paymentParams.quoteId}`);
    this.app.tick();
    this.notificationService.showNotification(err, NotificationType.Error, true);
    this.app.tick();
  }

  private registerLegacyCallbacks() {
    window.completeCREPayment = () => this.completeCREPayment();
    window.cancelCREPayment = () => this.cancelCREPayment();
    window.creHandleErrors = errors => this.creHandleErrors(errors);
    window.whatCVV2 = () => this.whatCVV2();
  }

  private registerHpfCallbacks() {
    window.startPayment = () => this.startHpfPayment();
    window.cancelPayment = reason => this.cancelHpfPayment(reason);
    window.completePayment = result => this.completeHpfPayment(result);
    window.handleTransactionErrors = errors => this.handleHpfPaymentErrors(errors);
    window.whatsThis = () => this.whatCVV2();
    window.hpfReady = () => this.onHpfReady();
  }

  private startHpfPayment() {
    return '';
  }

  private cancelHpfPayment(reason?: unknown) {
    this.cancelCREPayment();
    return reason;
  }

  private completeHpfPayment(result?: unknown) {
    this.completeCREPayment();
    return result;
  }

  private handleHpfPaymentErrors(errors: any) {
    if (!errors) {
      return;
    }

    const errorList = Array.isArray(errors) ? errors : [errors];

    const messages = errorList
      .map(error => {
        if (typeof error === 'object' && error !== null) {
          // Use gateway_message for status_code gatewayError, otherwise use status_message
          return error.status_code === gatewayErrorStatusCode ? error.gateway_message : error.status_message;
        }
        return null;
      })
      .filter(msg => msg !== undefined && msg !== null && msg !== '');

    if (messages.length > 0) {
      const errorMessage = messages.join('. ');
      this.apiService.logCustomErrors(`FE Chase failed: ${errorMessage} - QuoteId: ${this.paymentParams.quoteId}`);
      this.notificationService.showNotification(errorMessage, NotificationType.Error, true);
    }
  }

  private onHpfReady() {
    return true;
  }

  private ensureHpfCallbacksRegistered() {
    if (this.hpfCallbacksRegistered || !this.appInfoService.isNSLocaleSelected()) {
      return;
    }
    this.registerHpfCallbacks();
    this.hpfCallbacksRegistered = true;
  }
}
